/**
 * Game UI logic and rendering
 * Handles all UI updates and user interactions
 */

import { GameState, DominoTile } from './types';
import { WebSocketConnection } from './websocket';
import { GAME_TABLE_BACKGROUNDS, getRandomTableBackground } from './assets';
// DATE: 2025-12-17 | Author: IA + Luiz Couto - Custom modal for player profiles (replaced Tippy.js)
// DATE: 2025-12-20 | Added PendingActionData for dynamic account creation
import { getPlayerProfileModal, PlayerData, PendingActionData } from './components/PlayerProfileModal';

export class GameUI {
  private ws: WebSocketConnection;
  private gameState: Partial<GameState> | null = null;
  private playerId: string | null = null;
  private selectedTile: DominoTile | null = null;
  private gameStartSound: HTMLAudioElement;
  private tilePlacedSound: HTMLAudioElement;
  private isSpectator: boolean = false; // 2025-10-28: Track spectator mode
  private currentTableBackground: string; // Date: 2025-11-02 | Author: IA + Luiz Couto - Current table background

  // DATE: 2025-12-17 | Author: IA + Luiz Couto - Player identity for friend system
  private myNetcartasId: number | null = null;
  private myNickname: string = ''; // Nickname for modal identity
  
  constructor(ws: WebSocketConnection) {
    this.ws = ws;
    
    // Date: 2025-11-02 | Author: IA + Luiz Couto
    // Initialize with random table background
    this.currentTableBackground = getRandomTableBackground();
    this.applyTableBackground();
    
    // Date: 2025-10-19 | Author: IA + Luiz Couto
    // Load game sounds from local files (fixed CORS issue with R2 URLs)
    // Vite will handle these imports and bundle them properly
    this.gameStartSound = new Audio(new URL('./soundfx/game_start_metal_bell02.wav', import.meta.url).href);
    this.gameStartSound.volume = 0.5;
    this.gameStartSound.preload = 'auto'; // Preload the audio file
    this.gameStartSound.addEventListener('loadeddata', () => {
      console.log('✅ Game start sound loaded successfully');
    });
    this.gameStartSound.addEventListener('error', (e) => {
      console.error('❌ Failed to load game start sound:', e);
    });
    
    this.tilePlacedSound = new Audio(new URL('./soundfx/domino_piece_been_pu.mp3', import.meta.url).href);
    this.tilePlacedSound.volume = 0.4;
    this.tilePlacedSound.preload = 'auto'; // Preload the audio file
    this.tilePlacedSound.addEventListener('loadeddata', () => {
      console.log('✅ Tile placed sound loaded successfully');
    });
    this.tilePlacedSound.addEventListener('error', (e) => {
      console.error('❌ Failed to load tile placed sound:', e);
    });
    
    this.initializeEventListeners();
  }

  /**
   * Reset game state and UI for a fresh start
   * Date: 2025-11-02 | Author: IA + Luiz Couto
   * Cleans up all state variables and DOM elements to prevent pollution between games
   * Called when player leaves a game room and returns to lobby
   */
  public reset(): void {
    console.log('🧹 [2025-11-02] Resetting GameUI state and DOM');
    
    // Reset all state variables
    this.gameState = null;
    this.playerId = null;
    this.selectedTile = null;
    this.isSpectator = false;
    
    // Change table background for next game (visual refresh)
    this.currentTableBackground = getRandomTableBackground();
    this.applyTableBackground();
    
    // Clear spectator badge if it exists
    const spectatorBadge = document.getElementById('spectator-badge');
    if (spectatorBadge) {
      spectatorBadge.remove();
      console.log('🧹 Removed spectator badge');
    }
    
    // Clear chat messages
    const chatMessages = document.getElementById('chat-messages');
    if (chatMessages) {
      chatMessages.innerHTML = '';
      console.log('🧹 Cleared chat messages');
    }
    
    // Clear board
    const boardTiles = document.getElementById('board-tiles');
    if (boardTiles) {
      boardTiles.innerHTML = '';
      console.log('🧹 Cleared board tiles');
    }
    
    // Clear player hand
    const handContainer = document.getElementById('player-hand');
    if (handContainer) {
      handContainer.innerHTML = '';
      console.log('🧹 Cleared player hand');
    }
    
    // Clear all player containers
    const playerContainers = ['player-north', 'player-south', 'player-east', 'player-west'];
    playerContainers.forEach(containerId => {
      const container = document.getElementById(containerId);
      if (container) {
        container.innerHTML = '';
      }
    });
    console.log('🧹 Cleared player containers');
    
    // Clear hints
    const hintsContainer = document.getElementById('board-hints');
    if (hintsContainer) {
      hintsContainer.innerHTML = '';
      console.log('🧹 Cleared board hints');
    }
    
    // Hide and clear game over modal
    const modal = document.getElementById('game-over-modal');
    if (modal) {
      modal.classList.add('hidden');
      const scoresDiv = document.getElementById('final-scores');
      if (scoresDiv) {
        scoresDiv.innerHTML = '';
      }
      console.log('🧹 Cleared game over modal');
    }
    
    // DATE: 2025-11-09 | Author: IA + Luiz Couto
    // RESTORED: team-scores element is the CORRECT system - don't hide it permanently
    // It will be shown/hidden by renderTeamScores() based on game mode
    const teamScores = document.getElementById('team-scores');
    if (teamScores) {
      teamScores.classList.add('hidden');
      const teamAScore = document.getElementById('team-a-score');
      const teamBScore = document.getElementById('team-b-score');
      if (teamAScore) teamAScore.textContent = '0';
      if (teamBScore) teamBScore.textContent = '0';
      console.log('🧹 Reset team scores to 0');
    }
    
    // Reset game status text
    // Date: 2025-11-02 | Author: IA + Luiz Couto - Reset both header and center status
    // Date: 2025-11-07 | Author: IA + Luiz Couto - Added round number prefix
    const gameStatusTextHeader = document.getElementById('game-status-text-header');
    const gameStatusTextCenter = document.getElementById('game-status-text-center');
    if (gameStatusTextHeader) {
      gameStatusTextHeader.textContent = this.formatStatusWithRound('Aguardando jogadores...');
      gameStatusTextHeader.style.display = 'none';
    }
    if (gameStatusTextCenter) {
      gameStatusTextCenter.textContent = this.formatStatusWithRound('Aguardando jogadores...');
    }
    
    // Remove any orphaned animated tiles from DOM
    const animatedTiles = document.querySelectorAll('.animated-tile');
    animatedTiles.forEach(tile => tile.remove());
    if (animatedTiles.length > 0) {
      console.log(`🧹 Removed ${animatedTiles.length} orphaned animated tiles`);
    }
    
    console.log('✅ [2025-11-02] GameUI reset complete - ready for next game');
  }

  /**
   * Play game start sound
   */
  public playGameStartSound(): void {
    this.gameStartSound.currentTime = 0;
    this.gameStartSound.play().catch(err => {
      console.warn('Could not play game start sound:', err);
    });
    console.log('🔊 Playing game start sound');
  }

  /**
   * Play tile placed sound
   */
  public playTilePlacedSound(): void {
    this.tilePlacedSound.currentTime = 0;
    this.tilePlacedSound.play().catch(err => {
      console.warn('Could not play tile placed sound:', err);
    });
  }

  /**
   * Animate a tile from player position to the board
   * Date: 2025-10-19 | Author: IA + Luiz Couto
   * Creates a smooth animation showing the tile moving from the player's hand to the board
   */
  public animateTileToBoard(playerId: string, tile: { left: number; right: number }): void {
    if (!this.gameState || !this.gameState.players) return;

    // Get the player who played the tile
    const player = this.gameState.players[playerId];
    if (!player) return;

    // Determine player position (for 4-player mode, use their physical position)
    const playerCount = Object.keys(this.gameState.players).length;
    let startPosition: 'south' | 'north' | 'east' | 'west' = 'south';

    if (playerCount === 4) {
      // Get player index to determine position
      const playerIds = Object.keys(this.gameState.players);
      const myIndex = playerIds.indexOf(this.playerId || '');
      const playerIndex = playerIds.indexOf(playerId);
      
      // Calculate relative position (0=south/me, 1=east/right, 2=north/top, 3=west/left)
      const relativeIndex = (playerIndex - myIndex + 4) % 4;
      const positions: Array<'south' | 'east' | 'north' | 'west'> = ['south', 'east', 'north', 'west'];
      startPosition = positions[relativeIndex];
    } else {
      // 2-player mode: current player is south, opponent is north
      startPosition = playerId === this.playerId ? 'south' : 'north';
    }

    // Create the animated tile element
    // Date: 2025-11-02 | Author: IA + Luiz Couto - Use MEDIUM size for animation (works well on all screens)
    const animatedTile = document.createElement('div');
    animatedTile.className = 'board-tile regular-tile tile-animation';
    animatedTile.style.cssText = `
      width: 45px;
      height: 90px;
      background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%);
      border: 2px solid #333;
      border-radius: 8px;
      display: flex;
      flex-direction: column;
      justify-content: space-around;
      align-items: center;
      box-shadow: 0 4px 8px rgba(0,0,0,0.2);
    `;

    // Date: 2025-11-02 | Author: IA + Luiz Couto
    // Date: 2025-11-06 | Author: IA + Luiz Couto
    // Use pips instead of numbers for animation tile (matching new design)
    // Animation tiles are vertical (like player hand tiles)
    const topPips = this.createDominoPips(tile.left, true);
    const bottomPips = this.createDominoPips(tile.right, true);

    animatedTile.appendChild(topPips);
    animatedTile.appendChild(bottomPips);

    document.body.appendChild(animatedTile);

    // Get start and end positions
    const boardContainer = document.querySelector('.board-container');
    if (!boardContainer) {
      animatedTile.remove();
      return;
    }

    const boardRect = boardContainer.getBoundingClientRect();
    const boardCenterX = boardRect.left + boardRect.width / 2;
    const boardCenterY = boardRect.top + boardRect.height / 2;

    // Calculate start position based on player position
    let startX = window.innerWidth / 2;
    let startY = window.innerHeight / 2;

    switch (startPosition) {
      case 'south': // Bottom (current player)
        startX = window.innerWidth / 2;
        startY = window.innerHeight - 120;
        break;
      case 'north': // Top
        startX = window.innerWidth / 2;
        startY = 120;
        break;
      case 'east': // Right
        startX = window.innerWidth - 150;
        startY = window.innerHeight / 2;
        break;
      case 'west': // Left
        startX = 150;
        startY = window.innerHeight / 2;
        break;
    }

    // Set initial position
    animatedTile.style.left = `${startX}px`;
    animatedTile.style.top = `${startY}px`;
    animatedTile.style.transform = 'translate(-50%, -50%)';

    // Force a reflow to ensure the initial position is set
    animatedTile.offsetHeight;

    // Animate to board
    setTimeout(() => {
      animatedTile.style.left = `${boardCenterX}px`;
      animatedTile.style.top = `${boardCenterY}px`;
      animatedTile.style.transform = 'translate(-50%, -50%) scale(0.8)';
      animatedTile.style.opacity = '0';
    }, 10);

    // Remove after animation completes
    setTimeout(() => {
      animatedTile.remove();
    }, 650);
  }

  /**
   * Initialize UI event listeners
   */
  private initializeEventListeners(): void {
    // Date: 2025-11-02 | Author: IA + Luiz Couto
    // Change table background button
    document.getElementById('change-table-bg-btn')?.addEventListener('click', () => {
      this.cycleTableBackground();
    });
    
    // Ready button
    document.getElementById('ready-btn')?.addEventListener('click', () => {
      console.log('✅ Clicking ready button');
      this.ws.send({ type: 'ready' });
      
      const btn = document.getElementById('ready-btn') as HTMLButtonElement;
      btn.disabled = true;
      btn.textContent = 'Aguardando outros jogadores...';
      btn.style.opacity = '0.7';
      
      console.log('✅ Ready message sent to server');
    });

    // Action buttons
    document.getElementById('draw-btn')?.addEventListener('click', () => {
      console.log('🎴 [2025-10-26] Client: DRAW button clicked, sending message to server');
      console.log('🎴 [2025-10-26] Client: Current gameState:', this.gameState?.status, 'myTurn:', this.gameState?.currentPlayerId === this.playerId);
      this.ws.send({ type: 'draw' });
      console.log('🎴 [2025-10-26] Client: DRAW message sent');
    });

    document.getElementById('pass-btn')?.addEventListener('click', () => {
      console.log('⏭️ [2025-10-26] Client: PASS button clicked, sending message to server');
      this.ws.send({ type: 'pass' });
      console.log('⏭️ [2025-10-26] Client: PASS message sent');
    });

    // Restart button
    document.getElementById('restart-btn')?.addEventListener('click', () => {
      console.log('🔄 [2025-10-25] Client: Sending restart message to server...');
      this.ws.send({ type: 'restart' });
      console.log('🔄 [2025-10-25] Client: Restart message sent, showing waiting modal');
      // 2025-10-25: Don't close modal, transform it into "waiting for players" modal
      this.showWaitingForPlayersModal();
    });

    // Tile position modal buttons
    document.getElementById('play-left-btn')?.addEventListener('click', () => {
      if (this.selectedTile) {
        this.playTile(this.selectedTile.id, 'left');
      }
    });

    document.getElementById('play-right-btn')?.addEventListener('click', () => {
      if (this.selectedTile) {
        this.playTile(this.selectedTile.id, 'right');
      }
    });

    document.getElementById('cancel-play-btn')?.addEventListener('click', () => {
      this.hideTilePositionModal();
      this.selectedTile = null;
    });

    // Chat
    const chatInput = document.getElementById('chat-input') as HTMLInputElement;
    const sendChatBtn = document.getElementById('send-chat-btn');

    const sendChat = () => {
      const message = chatInput.value.trim();
      if (message) {
        this.ws.send({ type: 'chat', message });
        chatInput.value = '';
      }
    };

    sendChatBtn?.addEventListener('click', sendChat);
    chatInput?.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        sendChat();
      }
    });

    // DATE: 2025-10-31 | Author: IA + Luiz Couto
    // Chat minimize/maximize toggle (button only, not header)
    const chatToggleBtn = document.getElementById('chat-toggle-btn');
    chatToggleBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleChat();
    });
    
    // DATE: 2025-10-31 | Author: IA + Luiz Couto
    // Chat drag & drop functionality
    this.initializeChatDrag();
  }

  /**
   * Initialize chat drag functionality with position persistence
   * 
   * DATE: 2025-10-31 | Author: IA + Luiz Couto
   * Allows user to drag chat window on mobile/tablet (not on desktop sidebar)
   */
  private initializeChatDrag(): void {
    const chatContainer = document.getElementById('chat-container');
    const chatHeader = document.getElementById('chat-header');
    
    if (!chatContainer || !chatHeader) return;

    // Only enable drag on mobile/tablet (when chat is overlay, not sidebar)
    const isDragEnabled = () => window.innerWidth <= 1024;

    // Restore saved position from localStorage (mobile/tablet only)
    const restorePosition = () => {
      if (!isDragEnabled()) return;
      
      const savedPosition = localStorage.getItem('chat-position');
      if (savedPosition) {
        const { top, left, bottom, right } = JSON.parse(savedPosition);
        chatContainer.style.top = top;
        chatContainer.style.left = left;
        chatContainer.style.bottom = bottom;
        chatContainer.style.right = right;
        console.log('💬 [2025-10-31] Restored chat position from localStorage');
      }
    };
    
    restorePosition();

    let isDragging = false;
    let currentX = 0;
    let currentY = 0;
    let initialX = 0;
    let initialY = 0;

    let dragStartX = 0;
    let dragStartY = 0;
    let hasMoved = false;

    const dragStart = (e: MouseEvent) => {
      // DATE: 2025-10-31 | Author: IA + Luiz Couto
      // Only allow drag on mobile/tablet (sidebar mode on desktop = no drag)
      if (!isDragEnabled()) return;
      
      // Don't drag if clicking on buttons
      const target = e.target as HTMLElement;
      if (target.tagName === 'BUTTON' || target.closest('button')) return;

      // Get current position
      const rect = chatContainer.getBoundingClientRect();
      currentX = rect.left;
      currentY = rect.top;
      
      initialX = e.clientX - currentX;
      initialY = e.clientY - currentY;
      dragStartX = e.clientX;
      dragStartY = e.clientY;
      hasMoved = false;
      
      isDragging = true;
      chatHeader.style.cursor = 'grabbing';
      console.log('💬 [2025-10-31] Chat drag started (mobile/tablet mode)');
    };

    const drag = (e: MouseEvent) => {
      if (!isDragging) return;

      e.preventDefault();
      
      // Check if mouse moved significantly (more than 5px)
      const deltaX = Math.abs(e.clientX - dragStartX);
      const deltaY = Math.abs(e.clientY - dragStartY);
      if (deltaX > 5 || deltaY > 5) {
        hasMoved = true;
      }
      
      currentX = e.clientX - initialX;
      currentY = e.clientY - initialY;

      // Keep chat within viewport bounds
      const maxX = window.innerWidth - chatContainer.offsetWidth;
      const maxY = window.innerHeight - chatContainer.offsetHeight;
      
      currentX = Math.max(0, Math.min(currentX, maxX));
      currentY = Math.max(0, Math.min(currentY, maxY));

      // Update position
      chatContainer.style.left = `${currentX}px`;
      chatContainer.style.top = `${currentY}px`;
      chatContainer.style.right = 'auto';
      chatContainer.style.bottom = 'auto';
    };

    const dragEnd = () => {
      if (!isDragging) return;
      
      isDragging = false;
      chatHeader.style.cursor = 'grab';

      // Only save if actually moved (not just a click)
      if (hasMoved) {
        const position = {
          top: chatContainer.style.top,
          left: chatContainer.style.left,
          bottom: chatContainer.style.bottom,
          right: chatContainer.style.right
        };
        localStorage.setItem('chat-position', JSON.stringify(position));
        console.log('💬 [2025-10-31] Chat position saved:', position);
      }
    };

    chatHeader.addEventListener('mousedown', dragStart);
    document.addEventListener('mousemove', drag);
    document.addEventListener('mouseup', dragEnd);
  }

  /**
   * Set the player ID
   */
  setPlayerId(id: string): void {
    this.playerId = id;
  }

  /**
   * Set spectator mode
   * 
   * DATE: 2025-10-28
   * Disables all action buttons and shows spectator indicator
   */
  setSpectatorMode(isSpectator: boolean): void {
    this.isSpectator = isSpectator;
    console.log(`👁️ [2025-10-28] Spectator mode set to:`, isSpectator);
    
    // Date: 2025-11-02 | Author: IA + Luiz Couto
    // Hide player footer for spectators (they don't have hand or actions)
    const playerFooter = document.getElementById('player-footer');
    if (playerFooter) {
      if (isSpectator) {
        playerFooter.style.display = 'none';
        console.log('👁️ [2025-11-02] Player footer hidden for spectator');
      } else {
        playerFooter.style.display = 'block';
      }
    }
    
    if (isSpectator) {
      // Show spectator indicator
      const gameStatus = document.getElementById('game-status');
      if (gameStatus) {
        const spectatorBadge = document.createElement('div');
        spectatorBadge.id = 'spectator-badge';
        spectatorBadge.className = 'spectator-badge';
        spectatorBadge.innerHTML = '👁️ Você está assistindo';
        spectatorBadge.style.cssText = 'background: #673ab7; color: white; padding: 8px 16px; border-radius: 4px; margin-bottom: 10px; text-align: center; font-weight: bold;';
        gameStatus.prepend(spectatorBadge);
      }
    }
  }

  /**
   * Update the game state and render UI
   */
  updateGameState(state: Partial<GameState>): void {
    console.log('🔄 Game state updated:', {
      status: state.status,
      leftEnd: state.leftEnd,
      rightEnd: state.rightEnd,
      currentPlayerId: state.currentPlayerId,
      isMyTurn: state.currentPlayerId === this.playerId,
      boardLength: state.board?.length || 0
    });
    
    const oldStatus = this.gameState?.status;
    this.gameState = state;
    
    // 2025-10-25: Handle status transitions for restart flow
    if (oldStatus === 'finished' && state.status === 'waiting') {
      // Game was restarted, update the waiting modal with player status
      console.log('🔄 [2025-10-25] Client: Game restarted, updating waiting modal');
      this.updatePlayerReadyStatus();
    } else if (state.status === 'playing' && oldStatus === 'waiting') {
      // Game started (all players ready), close the modal
      console.log('🎮 [2025-10-25] Client: Game starting, hiding modal');
      this.hideGameOverModal();
    }
    
    this.render();
  }

  /**
   * Render the entire game UI
   */
  private render(): void {
    if (!this.gameState) return;

    this.renderPlayers();
    this.renderBoard();
    this.renderPlayerHand();
    this.renderGameStatus();
    this.renderActionButtons();
    this.updateBoneyardCount();
  }

  /**
   * Render opponent players
   * Supports both 2-player (legacy) and 4-player (partnership) layouts
   */
  private renderPlayers(): void {
    if (!this.gameState?.players) return;

    const playerCount = Object.keys(this.gameState.players).length;
    const gameTable = document.getElementById('game-table');

    console.log(`🎨 Rendering players: count=${playerCount}, gameMode=${this.gameState.gameMode}, hasTurnOrder=${!!this.gameState.turnOrder}, hasTeams=${!!this.gameState.teams}`);

    // DATE: 2025-12-17 | Author: IA + Luiz Couto
    // Hide all player position containers when room is empty (0 players)
    // This prevents showing empty gray panels when spectator enters empty room
    const northEl = document.getElementById('player-north');
    const southEl = document.getElementById('player-south');
    const westEl = document.getElementById('player-west');
    const eastEl = document.getElementById('player-east');

    if (playerCount === 0) {
      console.log('🎨 No players in room - hiding all player containers');
      if (northEl) northEl.style.display = 'none';
      if (southEl) southEl.style.display = 'none';
      if (westEl) westEl.style.display = 'none';
      if (eastEl) eastEl.style.display = 'none';
      return;
    }

    // DATE: 2025-10-31 | Author: IA + Luiz Couto
    // Apply layout class based on player count
    // CRITICAL: Ensure west/east are properly hidden for 2-player games
    if (gameTable) {
      if (playerCount === 2) {
        gameTable.classList.add('two-player-layout');
        // Force hide west/east for 2-player games
        if (westEl) westEl.style.display = 'none';
        if (eastEl) eastEl.style.display = 'none';
        // Show north/south for 2-player games
        if (northEl) northEl.style.display = '';
        if (southEl) southEl.style.display = '';
        console.log('🎨 Applied two-player layout (west/east HIDDEN, north/south VISIBLE)');
      } else {
        gameTable.classList.remove('two-player-layout');
        // Show all positions for 4-player games
        if (westEl) westEl.style.display = '';
        if (eastEl) eastEl.style.display = '';
        if (northEl) northEl.style.display = '';
        if (southEl) southEl.style.display = '';
        console.log('🎨 Applied four-player layout (all positions VISIBLE)');
      }
    }
    
    // Use four-corner layout for 4-player games
    if (playerCount === 4 && this.gameState.turnOrder) {
      console.log('🎨 Using four-player layout');
      this.renderFourPlayerLayout();
    } else {
      console.log('🎨 Using horizontal layout (2-3 players or no turnOrder)');
      // Use legacy horizontal layout for 2-3 player games
      this.renderHorizontalLayout();
    }
    
    // DATE: 2025-11-09 | Author: IA + Luiz Couto
    // RESTORED: renderTeamScores() - This system works perfectly for both modes!
    // Now updated to handle BOTH partnership and individual modes
    this.renderTeamScores();
    
    // DATE: 2025-11-09 | Author: IA + Luiz Couto
    // NEW: Apply team color borders to player containers
    this.applyTeamColors();

    // DATE: 2025-12-17 | Author: IA + Luiz Couto
    // Setup player profile tooltips with friend invite functionality
    this.setupPlayerTooltips();
  }

  /**
   * Render players in four-corner grid layout (partnership mode)
   * Current player always appears at South, partner at North, opponents at East/West
   * 
   * DATE: 2025-10-31 | Author: IA + Luiz Couto
   * Fixed: Spectators can now view all players correctly
   */
  private renderFourPlayerLayout(): void {
    const turnOrder = this.gameState!.turnOrder!;
    const teams = this.gameState!.teams;
    const players = this.gameState!.players;
    
    if (!players || !this.playerId) return;

    // DATE: 2025-10-31 | Author: IA + Luiz Couto
    // Note: West/East visibility now controlled by CSS .two-player-layout class
    // Find current player's index in turnOrder
    // For spectators, use index 0 so they see the game from first player's perspective
    const myIndex = turnOrder.indexOf(this.playerId);
    if (myIndex === -1) {
      if (this.isSpectator) {
        console.log('👁️ [2025-10-31] Spectator viewing game from first player perspective');
        // Render all players in their natural positions for spectator view
        this.renderFourPlayerLayoutForSpectator();
        return;
      } else {
      console.warn('🎨 Current player not found in turnOrder');
      return;
      }
    }
    
    // Reorder players relative to current player
    // Current player = South, Next = East, Opposite = North, Previous = West
    const relativePositions = ['south', 'east', 'north', 'west'];
    
    console.log(`🎨 My position in turnOrder: ${myIndex}, rotating view so I'm at South`);
    
    turnOrder.forEach((playerId, index) => {
      const player = players[playerId];
      if (!player) return;
      
      // Calculate relative position (current player always at index 0 = south)
      const relativeIndex = (index - myIndex + 4) % 4;
      const position = relativePositions[relativeIndex];
      
      console.log(`🎨 Player ${player.name}: turnOrder[${index}] → position=${position}`);
      
      const container = document.getElementById(`player-${position}`);
      if (!container) return;
      
      // Determine team color
      let teamClass = '';
      if (teams) {
        if (teams.teamA.includes(playerId)) teamClass = 'team-a';
        if (teams.teamB.includes(playerId)) teamClass = 'team-b';
      }
      
      // Add team color and current turn
      container.className = `player-position player-${position} ${teamClass}`;
      if (player.isCurrentTurn) container.classList.add('current-turn');
      container.setAttribute('data-player-id', playerId); // DATE: 2025-11-09 | For team colors

      // DATE: 2025-12-17 | Author: IA + Luiz Couto
      // Add player data for tooltip (friend invite popup)
      const playerDataForTooltip = {
        id: playerId,
        name: player.name,
        netcartasId: player.netcartasId || null,
        profileImageURL: player.profileImageURL || null
      };
      container.setAttribute('data-player', JSON.stringify(playerDataForTooltip));

      // Render player info (south position shows full hand via existing HTML)
      // Date: 2025-11-02 | Author: IA + Luiz Couto - Use new badge with avatar
      if (position !== 'south') {
        container.innerHTML = this.renderPlayerBadgeHTML(player, position);
      }
    });
    
    // Apply team color to south container (current player)
    const southContainer = document.getElementById('player-south');
    if (southContainer && this.playerId) {
      const myPlayer = players[this.playerId];
      if (teams) {
        let myTeamClass = '';
        if (teams.teamA.includes(this.playerId)) myTeamClass = 'team-a';
        if (teams.teamB.includes(this.playerId)) myTeamClass = 'team-b';
        southContainer.classList.add(myTeamClass);
      }
      southContainer.setAttribute('data-player-id', this.playerId); // DATE: 2025-11-09 | For team colors

      // DATE: 2025-12-17 | Author: IA + Luiz Couto
      // Add player data for tooltip (for consistency, even for self)
      if (myPlayer) {
        const playerDataForTooltip = {
          id: this.playerId,
          name: myPlayer.name,
          netcartasId: myPlayer.netcartasId || null,
          profileImageURL: myPlayer.profileImageURL || null
        };
        southContainer.setAttribute('data-player', JSON.stringify(playerDataForTooltip));
      }
    }
  }

  /**
   * Render players for spectator view (neutral perspective)
   * 
   * DATE: 2025-10-31 | Author: IA + Luiz Couto
   * Shows all 4 players in their natural positions without rotation
   */
  private renderFourPlayerLayoutForSpectator(): void {
    const turnOrder = this.gameState!.turnOrder!;
    const teams = this.gameState!.teams;
    const players = this.gameState!.players;
    
    if (!players) return;

    // Position mapping: index in turnOrder → grid position
    const positions: Array<'south' | 'east' | 'north' | 'west'> = ['south', 'east', 'north', 'west'];
    
    console.log('👁️ [2025-10-31] Rendering spectator view for 4 players');
    
    turnOrder.forEach((playerId, index) => {
      const player = players[playerId];
      if (!player) return;
      
      const position = positions[index];
      
      console.log(`👁️ [2025-10-31] Spectator view - Player ${player.name}: position=${position}`);
      
      const container = document.getElementById(`player-${position}`);
      if (!container) return;
      
      // Determine team color
      let teamClass = '';
      if (teams) {
        if (teams.teamA.includes(playerId)) teamClass = 'team-a';
        if (teams.teamB.includes(playerId)) teamClass = 'team-b';
      }
      
      // Add team color and current turn
      container.className = `player-position player-${position} ${teamClass}`;
      if (player.isCurrentTurn) container.classList.add('current-turn');
      container.setAttribute('data-player-id', playerId); // DATE: 2025-11-09 | For team colors

      // DATE: 2025-12-17 | Author: IA + Luiz Couto
      // Add player data for tooltip (friend invite popup)
      const playerDataForTooltip = {
        id: playerId,
        name: player.name,
        netcartasId: player.netcartasId || null,
        profileImageURL: player.profileImageURL || null
      };
      container.setAttribute('data-player', JSON.stringify(playerDataForTooltip));

      // Render all players the same way (showing tile count, not actual tiles)
      // Date: 2025-11-02 | Author: IA + Luiz Couto - Use new badge with avatar
      container.innerHTML = this.renderPlayerBadgeHTML(player, position);
    });
  }

  /**
   * Render tile icons for opponent
   */
  private renderTileIcons(count: number): string {
    return Array(count).fill(0).map(() => '<div class="tile-icon"></div>').join('');
  }

  /**
   * Generate player badge HTML with avatar (image or initials)
   * Date: 2025-11-02 | Author: IA + Luiz Couto
   * @param player - Player object with name, profileImageURL, etc.
   * @param position - Position on board (north, south, east, west)
   * @returns HTML string for player badge
   */
  private renderPlayerBadgeHTML(player: any, position: string): string {
    const disconnectedLabel = !player.connected ? ' (Desc.)' : '';
    const statusText = this.gameState?.status === 'waiting' 
      ? (player.isReady ? '✓ Pronto' : 'Aguardando') 
      : `${player.tiles.length} peças`;

    // Generate avatar HTML (image or initials)
    let avatarHTML = '';
    if (player.profileImageURL) {
      avatarHTML = `<img src="${player.profileImageURL}" alt="${player.name}" class="player-avatar-img" />`;
    } else {
      // Generate initials from player name (first letter of first 2 words)
      const initials = player.name
        .split(' ')
        .slice(0, 2)
        .map((word: string) => word.charAt(0).toUpperCase())
        .join('');
      avatarHTML = `<div class="player-avatar-initials">${initials}</div>`;
    }

    // For west/east positions, use vertical layout (avatar on top)
    // For north/south, use horizontal layout (avatar on left)
    if (position === 'west' || position === 'east') {
      return `
        <div class="player-badge-content player-badge-vertical">
          ${avatarHTML}
          <div class="player-info-compact">
            <div class="player-name-compact">${player.name}${disconnectedLabel}</div>
          </div>
          <div class="player-tiles-display">
            ${this.renderTileIcons(player.tileCount || player.tiles.length)}
          </div>
          <div class="player-status">${statusText}</div>
        </div>
      `;
    } else {
      return `
        <div class="player-badge-content player-badge-horizontal">
          ${avatarHTML}
          <div class="player-info-compact">
            <div class="player-name-compact">${player.name}${disconnectedLabel}</div>
            <div class="player-tiles-display">
              ${this.renderTileIcons(player.tileCount || player.tiles.length)}
            </div>
            <div class="player-status">${statusText}</div>
          </div>
        </div>
      `;
    }
  }

  /**
   * Render players in horizontal layout (2-3 player games)
   * Uses north position for opponent in 2-player games
   * 
   * DATE: 2025-10-31 | Author: IA + Luiz Couto
   * Fixed: Spectators can now view 2-player games correctly
   * Fixed: Clear west/east containers to prevent ghost players
   */
  private renderHorizontalLayout(): void {
    if (!this.gameState?.players) return;

    // DATE: 2025-10-31 | Author: IA + Luiz Couto
    // CRITICAL: Clear west/east containers for 2-player games
    // This prevents "ghost players" from previous 4-player games
    const westContainer = document.getElementById('player-west');
    const eastContainer = document.getElementById('player-east');
    if (westContainer) westContainer.innerHTML = '';
    if (eastContainer) eastContainer.innerHTML = '';
    console.log('🧹 [2025-10-31] Cleared west/east containers for 2-player game');

    // DATE: 2025-10-31 | Author: IA + Luiz Couto
    // For spectators, render both players as opponents
    if (this.isSpectator) {
      console.log('👁️ [2025-10-31] Spectator viewing 2-player game');
      const playerEntries = Object.entries(this.gameState.players);
      
      // Show first player in north, second in south
      playerEntries.forEach(([playerId, player], index) => {
        const position = index === 0 ? 'north' : 'south';
        const container = document.getElementById(`player-${position}`);
        if (!container) return;

        container.className = `player-position player-${position}`;
        if (player.isCurrentTurn) container.classList.add('current-turn');

        // DATE: 2025-12-17 | Author: IA + Luiz Couto
        // Add player data for tooltip (friend invite popup)
        const playerDataForTooltip = {
          id: playerId,
          name: player.name,
          netcartasId: player.netcartasId || null,
          profileImageURL: player.profileImageURL || null
        };
        container.setAttribute('data-player', JSON.stringify(playerDataForTooltip));

        // Date: 2025-11-02 | Author: IA + Luiz Couto - Use new badge with avatar
        container.innerHTML = this.renderPlayerBadgeHTML(player, position);
      });
    } else {
    // For 2-player games, use north and south positions
    Object.entries(this.gameState.players).forEach(([id, player]) => {
      if (id === this.playerId) {
        // Current player - update south container with team color if needed
        const southContainer = document.getElementById('player-south');
        if (southContainer) {
          southContainer.setAttribute('data-player-id', id); // DATE: 2025-11-09 | For team colors
          if (player.isCurrentTurn) {
            southContainer.classList.add('current-turn');
          } else {
            southContainer.classList.remove('current-turn');
          }

          // DATE: 2025-12-17 | Author: IA + Luiz Couto
          // Add player data for tooltip (for consistency, even for self)
          const playerDataForTooltip = {
            id: id,
            name: player.name,
            netcartasId: player.netcartasId || null,
            profileImageURL: player.profileImageURL || null
          };
          southContainer.setAttribute('data-player', JSON.stringify(playerDataForTooltip));
        }
      } else {
        // Opponent - render in north position
        const northContainer = document.getElementById('player-north');
        if (!northContainer) return;

        northContainer.className = 'player-position player-north';
        if (player.isCurrentTurn) northContainer.classList.add('current-turn');
        northContainer.setAttribute('data-player-id', id); // DATE: 2025-11-09 | For team colors

        // DATE: 2025-12-17 | Author: IA + Luiz Couto
        // Add player data for tooltip (friend invite popup)
        const playerDataForTooltip = {
          id: id,
          name: player.name,
          netcartasId: player.netcartasId || null,
          profileImageURL: player.profileImageURL || null
        };
        northContainer.setAttribute('data-player', JSON.stringify(playerDataForTooltip));

        // Date: 2025-11-02 | Author: IA + Luiz Couto - Use new badge with avatar
        northContainer.innerHTML = this.renderPlayerBadgeHTML(player, 'north');
      }
      });
    }

    // DATE: 2025-10-31 | Author: IA + Luiz Couto
    // Note: West/East visibility now controlled by CSS .two-player-layout class
  }

  /**
   * DATE: 2025-11-09 | Author: IA + Luiz Couto
   * Apply team color borders to player containers
   * Works for both partnership (2v2) and individual (1v1) modes
   */
  private applyTeamColors(): void {
    if (!this.gameState?.teams) {
      console.log('🎨 [2025-11-09] No teams defined, skipping team colors');
      return;
    }
    
    const positions = ['north', 'south', 'west', 'east'];
    
    const teams = this.gameState.teams;
    
    positions.forEach(position => {
      const container = document.getElementById(`player-${position}`);
      if (!container) return;
      
      // Remove any existing team classes
      container.classList.remove('team-a', 'team-b');
      
      // Find which player is in this position
      const playerId = container.getAttribute('data-player-id');
      
      if (playerId && teams && teams.teamA && teams.teamB) {
        // Determine team based on teams object
        const isTeamA = teams.teamA.includes(playerId);
        const isTeamB = teams.teamB.includes(playerId);
        
        if (isTeamA) {
          container.classList.add('team-a');
          console.log(`🔴 [2025-11-09] ${position}: Team A (vermelho)`);
        } else if (isTeamB) {
          container.classList.add('team-b');
          console.log(`🔵 [2025-11-09] ${position}: Team B (azul)`);
        }
      }
    });
  }

  /**
   * DATE: 2025-11-09 | Author: IA + Luiz Couto
   * Render team scores - UNIFIED SYSTEM for both modes
   * 
   * Works for:
   * - Partnership (2v2): Team A vs Team B
   * - Individual (1v1): Player 1 (Team A) vs Player 2 (Team B)
   */
  private renderTeamScores(): void {
    const scoresContainer = document.getElementById('team-scores');
    const teamAScore = document.getElementById('team-a-score');
    const teamBScore = document.getElementById('team-b-score');
    
    if (!scoresContainer || !teamAScore || !teamBScore) {
      console.warn('⚠️ [2025-11-09] renderTeamScores: Missing DOM elements');
      return;
    }
    
    // DATE: 2025-11-09 | Author: IA + Luiz Couto
    // UNIFIED SYSTEM: Sempre usar teamScores (partnership E individual)
    if (this.gameState?.teamScores) {
      scoresContainer.classList.remove('hidden');
      teamAScore.textContent = (this.gameState.teamScores.teamA ?? 0).toString();
      teamBScore.textContent = (this.gameState.teamScores.teamB ?? 0).toString();
      
      const mode = this.gameState.gameMode === 'partnership' ? 'PARTNERSHIP' : 'INDIVIDUAL';
      console.log(`📊 [2025-11-09] ${mode} scores: Time A=${this.gameState.teamScores.teamA}, Time B=${this.gameState.teamScores.teamB}`);
    } else {
      // No scores available yet
      console.log(`📊 [2025-11-09] No teamScores available yet`);
      scoresContainer.classList.add('hidden');
    }
    
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // DEPRECATED: playerScores logic removed (no longer used)
    // DATE: 2025-11-09 | All modes now use teamScores from server
    // Individual games: server creates teams automatically (player1=teamA, player2=teamB)
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  }

  /**
   * Render the game board with snake/zigzag pattern for multiple lines
   * 
   * DATE: 2025-10-31 | Author: IA + Luiz Couto
   * Creates a serpentine layout where tiles flow left-to-right on first line,
   * then right-to-left on second line, continuing the visual connection
   */
  private renderBoard(): void {
    const boardTiles = document.getElementById('board-tiles');
    if (!boardTiles || !this.gameState?.board) return;

    boardTiles.innerHTML = '';

    if (this.gameState.board.length === 0) {
      boardTiles.innerHTML = '<div style="color: #999;">Nenhuma peça jogada ainda</div>';
      return;
    }

    // DATE: 2025-10-31 | Author: IA + Luiz Couto
    // Date: 2025-11-02 | Author: IA + Luiz Couto - Responsive tile sizing system
    // Three patterns based on viewport width to prevent overflow:
    // - LARGE (>1300px): 110x55 tiles, 11px pips, ~125px per tile
    // - MEDIUM (1024-1300px): 90x45 tiles, 9px pips, ~105px per tile  
    // - SMALL (<1024px): 75x38 tiles, 8px pips, ~90px per tile
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const containerWidth = boardTiles.parentElement?.clientWidth || 1200;
    
    let avgTileSpace: number;
    let pattern: string;
    let tileSize: string;
    
    console.log('📐 [RESPONSIVE ENGINE] ════════════════════════════════');
    console.log(`📐 Viewport: ${viewportWidth}×${viewportHeight}px`);
    console.log(`📐 Container width: ${containerWidth}px`);
    
    let chatWidth: string;
    
    if (viewportWidth > 1300) {
      // LARGE pattern: Desktop with big screen
      // Chat: 320px sidebar
      avgTileSpace = 125; // 110px tile + 8px gap + margins
      pattern = 'LARGE';
      tileSize = '110×55px';
      chatWidth = '320px (full)';
      console.log(`📐 ✅ Pattern selected: ${pattern} (tiles: ${tileSize}, pips: 11px, chat: ${chatWidth})`);
    } else if (viewportWidth > 1024) {
      // MEDIUM pattern: Small laptops
      // Chat: 290px sidebar (10% smaller = +30px for game area)
      avgTileSpace = 100; // 90px tile + 8px gap + margins (optimized with smaller chat)
      pattern = 'MEDIUM';
      tileSize = '90×45px';
      chatWidth = '290px (-10%)';
      console.log(`📐 ✅ Pattern selected: ${pattern} (tiles: ${tileSize}, pips: 9px, chat: ${chatWidth})`);
    } else {
      // SMALL pattern: Tablet/mobile
      // Chat: floating overlay (no fixed sidebar)
      avgTileSpace = 90; // 75px tile + 6px gap + margins
      pattern = 'SMALL';
      tileSize = '75×38px';
      chatWidth = 'overlay (floating)';
      console.log(`📐 ✅ Pattern selected: ${pattern} (tiles: ${tileSize}, pips: 8px, chat: ${chatWidth})`);
    }
    
    const tilesPerRow = Math.max(5, Math.floor(containerWidth / avgTileSpace));
    const totalRows = Math.ceil(this.gameState.board.length / tilesPerRow);
    
    console.log(`📐 Space per tile: ${avgTileSpace}px`);
    console.log(`📐 Tiles per row: ${tilesPerRow}`);
    console.log(`📐 Total tiles: ${this.gameState.board.length} → ${totalRows} rows`);
    console.log('📐 ═════════════════════════════════════════════════════');

    // Create wrapper for all rows
    // DATE: 2025-10-31 | Author: IA + Luiz Couto  
    // Snake layout v3: Use CSS classes for alignment + flex-direction
    const boardWrapper = document.createElement('div');
    boardWrapper.className = 'domino-board-wrapper';
    
    // Split tiles into rows with "elbow" connectors
    // DATE: 2025-10-31 | Author: IA + Luiz Couto
    // Snake pattern with vertical connector tiles at line breaks
    const totalTiles = this.gameState.board.length;
    let currentRow = 0;
    let tilesProcessed = 0;
    
    while (tilesProcessed < totalTiles) {
      const tilesInThisRow = Math.min(tilesPerRow, totalTiles - tilesProcessed);
      const rowTiles = this.gameState.board.slice(tilesProcessed, tilesProcessed + tilesInThisRow);
      
      // Create row container
      const rowContainer = document.createElement('div');
      rowContainer.className = 'domino-train';
      
      if (currentRow === 0) {
        // FIRST ROW (0): No elbow, normal left-to-right flow
        rowContainer.classList.add('row-even');
        
        rowTiles.forEach((placedTile, index) => {
      const displayTile = placedTile.orientedTile || placedTile.tile;
          const globalIndex = tilesProcessed + index;
          const tileDiv = this.createBoardTile(displayTile, globalIndex);
          rowContainer.appendChild(tileDiv);
    });

        console.log(`🐍 [2025-10-31] Row ${currentRow}: FIRST ROW (no elbow) - ${tilesInThisRow} tiles`);
        
      } else if (currentRow % 2 === 1) {
        // ODD ROWS (1, 3, 5...): Right-aligned, reversed flow with elbow
        // DATE: 2025-10-31 | Author: IA + Luiz Couto
        // Elbow appears on RIGHT, tiles flow right-to-left
        
        rowContainer.classList.add('row-odd');
        
        // Add remaining tiles in REVERSE order (last → second)
        // FLIP the tile values (left↔right) for proper connection
        for (let i = rowTiles.length - 1; i >= 1; i--) {
          const placedTile = rowTiles[i];
          const originalTile = placedTile.orientedTile || placedTile.tile;
          
          const flippedTile: DominoTile = {
            id: originalTile.id,
            left: originalTile.right,
            right: originalTile.left
          };
          
          const globalIndex = tilesProcessed + i;
          const tileDiv = this.createBoardTile(flippedTile, globalIndex);
          rowContainer.appendChild(tileDiv);
        }
        
        // Add the elbow tile last (appears on RIGHT)
        const elbowTile = rowTiles[0];
        const elbowDisplay = elbowTile.orientedTile || elbowTile.tile;
        const elbowDiv = this.createBoardTile(elbowDisplay, tilesProcessed, true);
        rowContainer.appendChild(elbowDiv);
        console.log(`🔄 [2025-10-31] Row ${currentRow}: ODD with ELBOW on right`);
        
      } else {
        // EVEN ROWS (2, 4, 6...): Left-aligned, normal flow with elbow
        // DATE: 2025-10-31 | Author: IA + Luiz Couto
        // Elbow appears on LEFT, tiles flow left-to-right
        
        rowContainer.classList.add('row-even');
        
        // Add the elbow tile first (appears on LEFT)
        const elbowTile = rowTiles[0];
        const elbowDisplay = elbowTile.orientedTile || elbowTile.tile;
        const elbowDiv = this.createBoardTile(elbowDisplay, tilesProcessed, true);
        rowContainer.appendChild(elbowDiv);
        console.log(`🔄 [2025-10-31] Row ${currentRow}: EVEN with ELBOW on left`);
        
        // Add remaining tiles in normal order (second → last)
        for (let i = 1; i < rowTiles.length; i++) {
          const placedTile = rowTiles[i];
          const displayTile = placedTile.orientedTile || placedTile.tile;
          const globalIndex = tilesProcessed + i;
          const tileDiv = this.createBoardTile(displayTile, globalIndex);
          rowContainer.appendChild(tileDiv);
        }
      }
      
      boardWrapper.appendChild(rowContainer);
      tilesProcessed += tilesInThisRow;
      currentRow++;
    }

    boardTiles.appendChild(boardWrapper);
    
    // DATE: 2025-10-31 | Author: IA + Luiz Couto
    // Render play hints in the info bar (separate from board)
    this.renderPlayHints();
  }

  /**
   * Render play hints indicators in the info bar
   * 
   * DATE: 2025-10-31 | Author: IA + Luiz Couto
   * Shows where players can play their next tiles
   * TEMPORARY: Using numbers for debugging
   */
  private renderPlayHints(): void {
    const hintsContainer = document.getElementById('play-hints-container');
    if (!hintsContainer) return;

    hintsContainer.innerHTML = '';

    // Only show if game is playing and we have valid ends
    if (this.gameState?.status !== 'playing' || 
        this.gameState?.leftEnd === null || this.gameState?.leftEnd === undefined || 
        this.gameState?.rightEnd === null || this.gameState?.rightEnd === undefined) {
      return;
    }

    // DATE: 2025-10-31 | Author: IA + Luiz Couto
    // Using pips in indicators
      const leftEndDiv = document.createElement('div');
      leftEndDiv.className = 'board-end-indicator left';
    
    const leftArrow = document.createElement('span');
    leftArrow.textContent = '← ';
    leftEndDiv.appendChild(leftArrow);
    leftEndDiv.appendChild(this.createDominoPips(this.gameState.leftEnd));
    leftEndDiv.title = `Ponta esquerda: Jogue peças com ${this.gameState.leftEnd} aqui`;
    
      const rightEndDiv = document.createElement('div');
      rightEndDiv.className = 'board-end-indicator right';
    
    rightEndDiv.appendChild(this.createDominoPips(this.gameState.rightEnd));
    const rightArrow = document.createElement('span');
    rightArrow.textContent = ' →';
    rightEndDiv.appendChild(rightArrow);
    rightEndDiv.title = `Ponta direita: Jogue peças com ${this.gameState.rightEnd} aqui`;
    
    hintsContainer.appendChild(leftEndDiv);
    hintsContainer.appendChild(rightEndDiv);
  }

  /**
   * Create domino pips (dots) for a given value
   * 
   * DATE: 2025-10-31 | Author: IA + Luiz Couto
   * Date: 2025-11-06 | Author: IA + Luiz Couto
   * Renders traditional domino dots instead of numbers
   * Updated to support different orientations for number 6
   * 
   * @param value - The domino value (0-6)
   * @param isVertical - If true, uses vertical pattern for 6 (2 columns); if false, uses horizontal pattern (2 rows)
   * @returns HTML element with pip dots
   */
  private createDominoPips(value: number, isVertical: boolean = false): HTMLElement {
    const pipsContainer = document.createElement('div');
    pipsContainer.className = 'domino-pips';
    
    // Date: 2025-11-06 | Author: IA + Luiz Couto
    // Padrões de pontos do dominó tradicional
    // Número 6 tem dois padrões diferentes baseado na orientação da peça
    const pipPositions: { [key: number]: string[] } = {
      0: [], // Vazio
      1: ['center'], // 1 ponto no centro
      2: ['top-left', 'bottom-right'], // Diagonal
      3: ['top-left', 'center', 'bottom-right'], // Diagonal com centro
      4: ['top-left', 'top-right', 'bottom-left', 'bottom-right'], // 4 cantos
      5: ['top-left', 'top-right', 'center', 'bottom-left', 'bottom-right'], // 4 cantos + centro
      // Number 6 has different patterns based on tile orientation
      6: isVertical 
        ? ['top-left', 'top-right', 'middle-left', 'middle-right', 'bottom-left', 'bottom-right'] // Vertical: 2 columns (3 pips each)
        : ['top-left', 'top-center', 'top-right', 'bottom-left', 'bottom-center', 'bottom-right'] // Horizontal: 2 rows (3 pips each)
    };
    
    const positions = pipPositions[value] || [];
    
    positions.forEach(position => {
      const pip = document.createElement('div');
      pip.className = `pip-dot pip-${position}`;
      pipsContainer.appendChild(pip);
    });
    
    return pipsContainer;
  }

  /**
   * Create a board tile element with proper visual orientation
   * 
   * Handles two types of tile display following traditional domino rules:
   * 1. Regular tiles (e.g., 3|6): displayed horizontally
   * 2. Double tiles (e.g., 6|6): displayed vertically perpendicular to the line
   * 3. Elbow tiles: forced vertical for snake pattern turns
   * 
   * DATE: 2025-10-31 | Author: IA + Luiz Couto
   * TEMPORARY: Using numbers instead of pips for easier debugging of snake pattern
   * Once snake pattern is fixed, uncomment the pips version below
   * 
   * @param tile - The tile to display (should be pre-oriented by server)
   * @param index - Position in the board array (for potential future features)
   * @param forceVertical - Force tile to be vertical (for elbow/turn tiles)
   * @returns HTML element representing the tile
   */
  private createBoardTile(tile: DominoTile, _index: number, forceVertical: boolean = false): HTMLElement {
    const tileDiv = document.createElement('div');
    
    // DATE: 2025-10-31 | Author: IA + Luiz Couto
    // CRITICAL FIX: Only force vertical for elbows, NOT for all doubles!
    // In snake pattern, doubles can be horizontal too (only elbows are vertical)
    const shouldBeVertical = forceVertical;
    
    // DATE: 2025-10-31 | Author: IA + Luiz Couto
    // Using traditional domino pips (dots) for authentic look
    
    if (shouldBeVertical) {
      // VERTICAL TILES: Elbow connectors only!
      // Date: 2025-11-06 | Author: IA + Luiz Couto - Pass isVertical=true for correct 6 pattern
      tileDiv.className = 'board-tile double-tile';
      tileDiv.classList.add('elbow-tile'); // Mark as elbow for potential special styling
      
      const topPips = this.createDominoPips(tile.left, true);
      const bottomPips = this.createDominoPips(tile.right, true);

      tileDiv.appendChild(topPips);
      tileDiv.appendChild(bottomPips);
    } else {
      // HORIZONTAL TILES: Regular tiles
      // Date: 2025-11-06 | Author: IA + Luiz Couto - Pass isVertical=false for correct 6 pattern
      tileDiv.className = 'board-tile regular-tile';
      
      const leftPips = this.createDominoPips(tile.left, false);
      const rightPips = this.createDominoPips(tile.right, false);

      tileDiv.appendChild(leftPips);
      tileDiv.appendChild(rightPips);
    }

    return tileDiv;
  }

  /**
   * Render player hand (tiles)
   * 
   * DATE: 2025-10-31 | Author: IA + Luiz Couto
   * Updated: Spectators don't render player hand - they see all players from above
   */
  private renderPlayerHand(): void {
    const handContainer = document.getElementById('player-hand');
    if (!handContainer || !this.gameState?.players || !this.playerId) return;

    // DATE: 2025-10-31 | Author: IA + Luiz Couto
    // Spectators don't have tiles - clear the hand area
    if (this.isSpectator) {
      handContainer.innerHTML = '<div style="text-align: center; color: #999; padding: 1rem;">Modo Espectador - visualizando todos os jogadores</div>';
      return;
    }

    const player = this.gameState.players[this.playerId];
    if (!player) return;

    handContainer.innerHTML = '';

    // Date: 2025-11-02 | Author: IA + Luiz Couto
    // Update player info in footer
    const nameDisplay = document.getElementById('player-name-display');
    const tilesCount = document.getElementById('player-tiles-count');
    const avatarFooter = document.getElementById('player-avatar-footer');
    
    if (nameDisplay) nameDisplay.textContent = player.name;
    if (tilesCount) tilesCount.textContent = `${player.tiles.length} peças`;
    
    // DATE: 2025-12-21 | Author: IA + Luiz Couto
    // Render avatar in footer (image if available, otherwise initials)
    if (avatarFooter && player.name) {
      if (player.profileImageURL) {
        // Render avatar image
        avatarFooter.innerHTML = `<img src="${player.profileImageURL}" alt="${player.name}" class="player-avatar-img" />`;
        avatarFooter.classList.add('has-image');
      } else {
        // Fallback to initials
        const initials = player.name
          .split(' ')
          .map(word => word[0])
          .join('')
          .toUpperCase()
          .substring(0, 2);
        avatarFooter.innerHTML = '';
        avatarFooter.textContent = initials;
        avatarFooter.classList.remove('has-image');
      }

      // DATE: 2025-12-19 | Author: Claude + Luiz Couto
      // Add click handler to show own profile popup
      this.setupFooterProfileClick(player);
    }

    // Render tiles
    player.tiles.forEach((tile) => {
      if (tile) { // tile might be null for other players
        const tileDiv = this.createPlayerTile(tile);
        handContainer.appendChild(tileDiv);
      }
    });
  }

  /**
   * Create a player tile element
   * Date: 2025-11-02 | Author: IA + Luiz Couto - Updated to use pips instead of numbers
   */
  private createPlayerTile(tile: DominoTile): HTMLElement {
    const tileDiv = document.createElement('div');
    tileDiv.className = 'player-tile';
    tileDiv.dataset.tileId = tile.id;

    // Date: 2025-11-02 | Author: IA + Luiz Couto
    // Date: 2025-11-06 | Author: IA + Luiz Couto
    // Use the same pip rendering as board tiles for consistent look
    // Player tiles are always vertical, so pass isVertical=true
    const topPips = this.createDominoPips(tile.left, true);
    const bottomPips = this.createDominoPips(tile.right, true);

    tileDiv.appendChild(topPips);
    tileDiv.appendChild(bottomPips);

    // Add click handler
    tileDiv.addEventListener('click', () => {
      this.handleTileClick(tile);
    });

    // Disable if not player's turn or game not playing
    if (this.gameState?.status !== 'playing' || 
        this.gameState?.currentPlayerId !== this.playerId) {
      tileDiv.classList.add('disabled');
    }

    return tileDiv;
  }

  /**
   * Handle tile click
   */
  private handleTileClick(tile: DominoTile): void {
    if (this.gameState?.status !== 'playing' || 
        this.gameState?.currentPlayerId !== this.playerId) {
      return;
    }

    console.log(`🎯 Attempting to play tile: ${tile.left}|${tile.right}`);
    console.log(`📋 Current board state:`, {
      leftEnd: this.gameState?.leftEnd,
      rightEnd: this.gameState?.rightEnd,
      boardLength: this.gameState?.board?.length
    });

    // Check if tile can be played
    const canPlayLeft = this.canPlayOnEnd(tile, 'left');
    const canPlayRight = this.canPlayOnEnd(tile, 'right');

    console.log(`🎮 Play validation:`, {
      canPlayLeft,
      canPlayRight,
      tileValues: [tile.left, tile.right]
    });

    if (!canPlayLeft && !canPlayRight) {
      const errorMsg = `Esta peça (${tile.left}|${tile.right}) não pode ser jogada. Board ends: ${this.gameState?.leftEnd}|${this.gameState?.rightEnd}`;
      console.log('❌', errorMsg);
      this.showError(errorMsg);
      return;
    }

    // If tile can be played on both ends, show position modal
    if (canPlayLeft && canPlayRight) {
      this.selectedTile = tile;
      this.showTilePositionModal();
    } else {
      // Play on the only valid position
      const position = canPlayLeft ? 'left' : 'right';
      console.log(`✅ Playing tile ${tile.left}|${tile.right} on ${position} end`);
      this.playTile(tile.id, position);
    }
  }

  /**
   * Check if a tile can be played on a specific end
   */
  private canPlayOnEnd(tile: DominoTile, position: 'left' | 'right'): boolean {
    if (!this.gameState) return false;

    // First tile can be played anywhere
    if (this.gameState.leftEnd === null || this.gameState.rightEnd === null) {
      return true;
    }

    const endValue = position === 'left' ? this.gameState.leftEnd : this.gameState.rightEnd;
    return tile.left === endValue || tile.right === endValue;
  }

  /**
   * Play a tile
   */
  private playTile(tileId: string, position: 'left' | 'right'): void {
    this.ws.send({ type: 'play', tileId, position });
    this.hideTilePositionModal();
    this.selectedTile = null;
  }

  /**
   * Format status text with round number prefix
   * Date: 2025-11-07 | Author: IA + Luiz Couto
   * 
   * Formats game status with round number for better game tracking
   * Example: "Rodada 3 - Jogo em andamento"
   */
  private formatStatusWithRound(baseStatus: string): string {
    if (this.gameState?.roundNumber && this.gameState.roundNumber > 0) {
      return `Rodada ${this.gameState.roundNumber} - ${baseStatus}`;
    }
    return baseStatus;
  }

  /**
   * Render game status
   * Date: 2025-11-02 | Author: IA + Luiz Couto - Updated to control both header and center status
   * Date: 2025-11-07 | Author: IA + Luiz Couto - Added round number to status
   */
  private renderGameStatus(): void {
    const statusTextHeader = document.getElementById('game-status-text-header');
    const statusTextCenter = document.getElementById('game-status-text-center');
    const turnIndicator = document.getElementById('turn-indicator');
    const centerOverlay = document.getElementById('game-center-overlay');
    
    if (!turnIndicator || !this.gameState) return;

    if (this.gameState.status === 'waiting') {
      const playerCount = Object.keys(this.gameState.players || {}).length;
      const maxPlayers = this.gameState.gameMode === 'individual' ? 2 : 4;
      
      // Date: 2025-11-02 | Author: IA + Luiz Couto - Update center status (visible in overlay)
      // Date: 2025-11-07 | Author: IA + Luiz Couto - Added round number prefix
      if (statusTextCenter) {
        const baseStatus = `Aguardando jogadores... (${playerCount}/${maxPlayers})`;
        statusTextCenter.textContent = this.formatStatusWithRound(baseStatus);
      }
      
      // Date: 2025-11-02 | Author: IA + Luiz Couto - Hide header status when waiting
      if (statusTextHeader) {
        statusTextHeader.style.display = 'none';
      }
      
      turnIndicator.textContent = '';
      turnIndicator.classList.remove('your-turn');
      
      // Date: 2025-11-02 | Author: IA + Luiz Couto - Show center overlay when waiting
      if (centerOverlay) {
        centerOverlay.style.display = 'flex';
      }
    } else if (this.gameState.status === 'playing') {
      // Date: 2025-11-02 | Author: IA + Luiz Couto - Hide center overlay when playing
      if (centerOverlay) {
        centerOverlay.style.display = 'none';
      }
      
      // Date: 2025-11-02 | Author: IA + Luiz Couto - Show header status when playing
      // Date: 2025-11-07 | Author: IA + Luiz Couto - Added round number prefix
      if (statusTextHeader) {
        statusTextHeader.style.display = 'block';
        statusTextHeader.textContent = this.formatStatusWithRound('Jogo em andamento');
      }
      
      if (this.gameState.currentPlayerId === this.playerId) {
        turnIndicator.textContent = 'Sua vez!';
        turnIndicator.classList.add('your-turn');
      } else {
        const currentPlayer = this.gameState.players?.[this.gameState.currentPlayerId || ''];
        turnIndicator.textContent = `Vez de ${currentPlayer?.name || 'Jogador'}`;
        turnIndicator.classList.remove('your-turn');
      }
    } else if (this.gameState.status === 'finished') {
      // Date: 2025-11-02 | Author: IA + Luiz Couto - Hide center overlay when finished
      if (centerOverlay) {
        centerOverlay.style.display = 'none';
      }
      
      // Date: 2025-11-02 | Author: IA + Luiz Couto - Show header status when finished
      if (statusTextHeader) {
        statusTextHeader.style.display = 'block';
        statusTextHeader.textContent = 'Jogo finalizado';
      }
      
      turnIndicator.textContent = '';
      turnIndicator.classList.remove('your-turn');
    }
  }

  /**
   * Render action buttons
   */
  private renderActionButtons(): void {
    const actionButtons = document.getElementById('action-buttons');
    const readyBtn = document.getElementById('ready-btn') as HTMLButtonElement;
    
    if (!actionButtons || !readyBtn || !this.gameState) return;

    // 2025-10-28: Spectators cannot interact - hide all action buttons
    if (this.isSpectator) {
      readyBtn.classList.add('hidden');
      actionButtons.classList.add('hidden');
      return;
    }

    // Show/hide ready button
    if (this.gameState.status === 'waiting') {
      readyBtn.classList.remove('hidden');
      const player = this.gameState.players?.[this.playerId || ''];
      readyBtn.disabled = player?.isReady || false;
    } else {
      readyBtn.classList.add('hidden');
    }

    // Show/hide action buttons
    if (this.gameState.status === 'playing' && 
        this.gameState.currentPlayerId === this.playerId) {
      actionButtons.classList.remove('hidden');
      
      // Check if player can draw
      const drawBtn = document.getElementById('draw-btn') as HTMLButtonElement;
      if (drawBtn) {
        drawBtn.disabled = (this.gameState.boneyardCount || 0) === 0;
      }
    } else {
      actionButtons.classList.add('hidden');
    }
  }

  /**
   * Update boneyard count display
   */
  private updateBoneyardCount(): void {
    const boneyardCount = document.getElementById('boneyard-count');
    if (boneyardCount && this.gameState) {
      boneyardCount.textContent = (this.gameState.boneyardCount || 0).toString();
    }
  }

  /**
   * Show tile position selection modal
   */
  private showTilePositionModal(): void {
    const modal = document.getElementById('tile-position-modal');
    if (modal) {
      modal.classList.remove('hidden');
    }
  }

  /**
   * Hide tile position selection modal
   */
  private hideTilePositionModal(): void {
    const modal = document.getElementById('tile-position-modal');
    if (modal) {
      modal.classList.add('hidden');
    }
  }

  /**
   * Show game over modal
   * 
   * DATE: 2025-10-31 | Author: IA + Luiz Couto
   * Enhanced for partnership mode: shows team scores prominently
   * Updated: 2025-10-31 - Spectators don't see game over modal
   */
  /**
   * DATE: 2025-11-06 | Author: IA + Luiz Couto
   * CRITICAL FIX: Atualizado para sistema de rodadas múltiplas
   * Mostra placar ACUMULADO, não apenas da rodada final
   */
  showGameOverModal(winnerId: string, reason: string): void {
    console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
    console.log(`🎨 [CLIENT] showGameOverModal() CALLED`);
    console.log(`🎨 winnerId: ${winnerId}`);
    console.log(`🎨 reason: ${reason}`);
    
    const modal = document.getElementById('game-over-modal');
    const message = document.getElementById('game-over-message');
    const scoresDiv = document.getElementById('final-scores');
    
    if (!modal || !message || !scoresDiv || !this.gameState) {
      console.error(`❌ Missing elements or gameState!`);
      return;
    }

    console.log(`📊 gameState.roundNumber: ${this.gameState.roundNumber}`);
    console.log(`📊 gameState.pointGoal: ${this.gameState.pointGoal}`);
    console.log(`📊 gameState.teamScores:`, this.gameState.teamScores);
    console.log(`📊 gameState.playerScores:`, this.gameState.playerScores);

    // DATE: 2025-10-31 | Author: IA + Luiz Couto
    // Spectators should not see the game over modal
    if (this.isSpectator) {
      console.log('👁️ [2025-10-31] Spectator - not showing game over modal');
      return;
    }

    const winner = this.gameState.players?.[winnerId];
    
    // DATE: 2025-11-06 | Author: IA + Luiz Couto
    // Verificar se é sistema de rodadas múltiplas (roundNumber > 0)
    const isMultiRoundSystem = this.gameState.roundNumber && this.gameState.roundNumber > 0;
    console.log(`🔍 isMultiRoundSystem: ${isMultiRoundSystem}`);

    // DATE: 2025-10-31 | Author: IA + Luiz Couto
    // For partnership mode, show team result in title
    let titleMessage = winner ? `${winner.name} venceu! ${reason}` : reason;
    
    scoresDiv.innerHTML = '';
    
    // Partnership mode: Show team scores prominently
    if (this.gameState.teams && this.gameState.teamScores) {
      console.log('🏁 [2025-10-31] Client: Showing partnership game results');
      console.log('🏁 [2025-10-31] Team scores:', this.gameState.teamScores);
      console.log('🏁 [2025-11-06] Multi-round system:', isMultiRoundSystem);
      
      // Determine which team won
      const winnerTeam = this.gameState.teams.teamA.includes(winnerId) ? 'A' : 'B';
      
      // DATE: 2025-11-06 | Author: IA + Luiz Couto
      // CRITICAL: Usar pontos ACUMULADOS (teamScores já tem os valores corretos)
      const winnerTeamScore = winnerTeam === 'A' ? this.gameState.teamScores.teamA : this.gameState.teamScores.teamB;
      const loserTeamScore = winnerTeam === 'A' ? this.gameState.teamScores.teamB : this.gameState.teamScores.teamA;
      
      console.log(`🏆 Winner Team: ${winnerTeam}`);
      console.log(`🏆 Winner Team Score: ${winnerTeamScore} pontos`);
      console.log(`🏆 Loser Team Score: ${loserTeamScore} pontos`);
      
      titleMessage = `Time ${winnerTeam} venceu! ${reason}`;
      
      // DATE: 2025-11-06 | Author: IA + Luiz Couto
      // Show team scores at the top (usando PONTOS ACUMULADOS)
      // CRITICAL FIX: Cada box deve mostrar SEU PRÓPRIO score, não winner/loser
      const teamAScore = this.gameState.teamScores.teamA;
      const teamBScore = this.gameState.teamScores.teamB;
      
      console.log(`📊 Rendering modal - teamA: ${teamAScore}, teamB: ${teamBScore}`);
      
      const teamScoresHTML = `
        <div class="team-scores-final">
          <div class="team-score-box ${winnerTeam === 'A' ? 'winner-team' : ''}">
            <div class="team-label">Time A (Vermelho)</div>
            <div class="team-score">${teamAScore} pontos</div>
            ${winnerTeam === 'A' ? '<div class="winner-badge">🏆 Vencedor</div>' : ''}
          </div>
          <div class="team-score-box ${winnerTeam === 'B' ? 'winner-team' : ''}">
            <div class="team-label">Time B (Azul)</div>
            <div class="team-score">${teamBScore} pontos</div>
            ${winnerTeam === 'B' ? '<div class="winner-badge">🏆 Vencedor</div>' : ''}
          </div>
        </div>
        ${isMultiRoundSystem ? `
          <div class="score-divider">🏆 Vitória em ${this.gameState.roundNumber} Rodadas!</div>
        ` : `
          <div class="score-divider">Peças Restantes por Jogador</div>
        `}
      `;
      
      scoresDiv.innerHTML = teamScoresHTML;
      
      // DATE: 2025-11-06 | Author: IA + Luiz Couto
      // APENAS mostrar peças restantes se NÃO for sistema de rodadas múltiplas
      if (!isMultiRoundSystem) {
        // Show individual player tile counts (grouped by team)
        const teamAPlayers = this.gameState.teams.teamA.map(id => this.gameState!.players![id]);
        const teamBPlayers = this.gameState.teams.teamB.map(id => this.gameState!.players![id]);
        
        teamAPlayers.forEach(player => {
          const scoreItem = document.createElement('div');
          scoreItem.className = 'score-item team-a-player';
          scoreItem.innerHTML = `
            <span>🔴 ${player.name}</span>
            <span>${player.score} pts (${player.tiles.length} peças)</span>
          `;
          scoresDiv.appendChild(scoreItem);
        });
        
        teamBPlayers.forEach(player => {
          const scoreItem = document.createElement('div');
          scoreItem.className = 'score-item team-b-player';
          scoreItem.innerHTML = `
            <span>🔵 ${player.name}</span>
            <span>${player.score} pts (${player.tiles.length} peças)</span>
          `;
          scoresDiv.appendChild(scoreItem);
        });
      }
      
    } else {
      // Individual mode: Show player scores normally
      console.log('🏁 [2025-10-31] Client: Showing individual game results');
      
      if (this.gameState.players) {
        // DATE: 2025-11-06 | Author: IA + Luiz Couto
        // Se sistema de rodadas múltiplas, mostrar playerScores acumulados
        if (isMultiRoundSystem && this.gameState.playerScores) {
          Object.entries(this.gameState.playerScores)
            .sort(([, a], [, b]) => b - a)
            .forEach(([id, score]) => {
              const player = this.gameState?.players?.[id];
              const scoreItem = document.createElement('div');
              scoreItem.className = 'score-item';
              if (id === winnerId) {
                scoreItem.classList.add('winner');
              }
              scoreItem.innerHTML = `
                <span>${player?.name || 'Desconhecido'}</span>
                <span>${score} pontos acumulados</span>
              `;
              scoresDiv.appendChild(scoreItem);
            });
        } else {
          // Sistema antigo: mostrar player.score (peças restantes)
          Object.entries(this.gameState.players)
            .sort(([, a], [, b]) => a.score - b.score)
            .forEach(([id, player]) => {
              const scoreItem = document.createElement('div');
              scoreItem.className = 'score-item';
              if (id === winnerId) {
                scoreItem.classList.add('winner');
              }
              scoreItem.innerHTML = `
                <span>${player.name}</span>
                <span>${player.score} pontos (${player.tiles.length} peças)</span>
              `;
              scoresDiv.appendChild(scoreItem);
            });
        }
      }
    }

    message.textContent = titleMessage;
    modal.classList.remove('hidden');
  }

  /**
   * Show waiting for players modal (after restart)
   * 
   * DATE: 2025-10-25
   * Transforms the game-over modal into a "waiting for ready" screen
   */
  private showWaitingForPlayersModal(): void {
    console.log('🔄 [2025-10-25] Client: Showing waiting for players modal');
    
    const modal = document.getElementById('game-over-modal');
    const message = document.getElementById('game-over-message');
    const scoresDiv = document.getElementById('final-scores');
    
    if (!modal || !message || !scoresDiv) return;

    // Change modal title
    message.textContent = 'Preparando Nova Partida...';
    
    // DATE: 2025-11-06 | Author: IA + Luiz Couto
    // Task 3.5.1: Reset accumulated scores when restarting
    this.resetAccumulatedScores();
    
    // Show player ready status
    this.updatePlayerReadyStatus();
    
    modal.classList.remove('hidden');
  }

  /**
   * Update player ready status in modal
   * 
   * DATE: 2025-10-25
   * Updated: 2025-10-31 | Author: IA + Luiz Couto - Fixed spectator handling
   */
  private updatePlayerReadyStatus(): void {
    const scoresDiv = document.getElementById('final-scores');
    if (!scoresDiv || !this.gameState) return;

    scoresDiv.innerHTML = '';
    
    // DATE: 2025-10-31 | Author: IA + Luiz Couto
    // Spectators should not see the ready modal - hide it immediately
    if (this.isSpectator) {
      console.log('👁️ [2025-10-31] Spectator detected - hiding ready modal');
      this.hideGameOverModal();
      return;
    }
    
    if (this.gameState.players) {
      Object.entries(this.gameState.players).forEach(([_id, player]) => {
        const statusItem = document.createElement('div');
        statusItem.className = 'score-item';
        
        const readyIcon = player.isReady ? '✅' : '⏳';
        const readyText = player.isReady ? 'Pronto' : 'Aguardando...';
        
        statusItem.innerHTML = `
          <span>${readyIcon} ${player.name}</span>
          <span style="color: ${player.isReady ? '#4caf50' : '#999'}">${readyText}</span>
        `;
        scoresDiv.appendChild(statusItem);
      });
      
      // Add "Estou Pronto!" button for current player if not ready
      if (this.playerId) {
        const currentPlayer = this.gameState.players[this.playerId];
        if (currentPlayer && !currentPlayer.isReady) {
          const readyBtn = document.createElement('button');
          readyBtn.className = 'btn btn-primary';
          readyBtn.textContent = 'Estou Pronto!';
          readyBtn.style.marginTop = '20px';
          readyBtn.style.width = '100%';
          readyBtn.addEventListener('click', () => {
            console.log('✅ [2025-10-25] Client: Sending ready message');
            this.ws.send({ type: 'ready' });
          });
          scoresDiv.appendChild(readyBtn);
        }
      }
    }
  }

  /**
   * Hide game over modal
   */
  private hideGameOverModal(): void {
    const modal = document.getElementById('game-over-modal');
    if (modal) {
      modal.classList.add('hidden');
    }
  }

  /**
   * Add a chat message to the display
   * Date: 2025-12-29 | Author: IA + Luiz Couto - Added timestamp for user messages
   */
  addChatMessage(playerId: string, playerName: string, message: string): void {
    const chatMessages = document.getElementById('chat-messages');
    if (!chatMessages) return;

    const messageDiv = document.createElement('div');
    messageDiv.className = 'chat-message';

    // Get current time formatted as HH:MM
    const now = new Date();
    const timeStr = now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    if (playerId === 'system') {
      messageDiv.classList.add('system');
      messageDiv.textContent = message;
    } else {
      // User message with timestamp on separate line
      messageDiv.innerHTML = `
        <div class="chat-message-content"><strong>${playerName}:</strong>${message}</div>
        <span class="chat-message-time">${timeStr}</span>
      `;
    }

    chatMessages.appendChild(messageDiv);
    chatMessages.scrollTop = chatMessages.scrollHeight;
  }

  /**
   * Show an error message in the turn indicator
   * DATE: 2025-12-19 | Author: Claude + Luiz Couto
   * Dynamic turn indicator that shows errors temporarily
   */
  showError(message: string): void {
    console.error(message);

    // Show error in turn indicator instead of chat
    const turnIndicator = document.getElementById('turn-indicator');
    if (turnIndicator) {
      // Show error state
      turnIndicator.textContent = this.formatErrorMessage(message);
      turnIndicator.classList.remove('your-turn');
      turnIndicator.classList.add('error-state');

      // Return to normal state after 3 seconds
      setTimeout(() => {
        turnIndicator.classList.remove('error-state');
        // Restore the correct turn state
        this.renderGameStatus();
      }, 3000);
    }
  }

  /**
   * Format error message for turn indicator display
   * DATE: 2025-12-19 | Author: Claude + Luiz Couto
   */
  private formatErrorMessage(message: string): string {
    // Simplify common error messages for better UX
    if (message.includes('não pode ser jogada')) {
      const match = message.match(/\((\d)\|(\d)\)/);
      if (match) {
        return `Peça ${match[1]}|${match[2]} inválida!`;
      }
      return 'Peça inválida!';
    }
    if (message.includes('jogadas válidas disponíveis')) {
      return 'Você pode jogar!';
    }
    // Truncate long messages
    return message.length > 25 ? message.substring(0, 22) + '...' : message;
  }

  /**
   * Update room ID display
   */
  updateRoomId(roomId: string): void {
    const display = document.getElementById('room-id-display');
    if (display) {
      display.textContent = roomId;
    }
  }

  /**
   * Toggle chat minimize/maximize
   * 
   * DATE: 2025-10-31 | Author: IA + Luiz Couto
   * Minimizes or maximizes the chat window
   */
  private toggleChat(): void {
    const chatContainer = document.getElementById('chat-container');
    const chatToggleBtn = document.getElementById('chat-toggle-btn');
    
    if (!chatContainer || !chatToggleBtn) return;
    
    const isMinimized = chatContainer.classList.contains('minimized');
    
    if (isMinimized) {
      // Maximize
      chatContainer.classList.remove('minimized');
      chatToggleBtn.textContent = '−';
      chatToggleBtn.title = 'Minimizar chat';
      console.log('💬 [2025-10-31] Chat maximized');
    } else {
      // Minimize
      chatContainer.classList.add('minimized');
      chatToggleBtn.textContent = '+';
      chatToggleBtn.title = 'Maximizar chat';
      console.log('💬 [2025-10-31] Chat minimized');
    }
  }

  /**
   * Apply current table background to game table
   * Date: 2025-11-02 | Author: IA + Luiz Couto
   */
  private applyTableBackground(): void {
    const gameTable = document.getElementById('game-table');
    if (gameTable) {
      gameTable.style.backgroundImage = `url('${this.currentTableBackground}')`;
      console.log('🎨 [Table Background] Applied:', this.currentTableBackground);
    }
  }

  /**
   * Cycle through table backgrounds
   * Date: 2025-11-02 | Author: IA + Luiz Couto
   */
  private cycleTableBackground(): void {
    const backgrounds = Object.values(GAME_TABLE_BACKGROUNDS) as string[];
    const currentIndex = backgrounds.indexOf(this.currentTableBackground);
    const nextIndex = (currentIndex + 1) % backgrounds.length;
    this.currentTableBackground = backgrounds[nextIndex];
    this.applyTableBackground();
    
    // Get background name for feedback
    const bgName = Object.keys(GAME_TABLE_BACKGROUNDS)[nextIndex];
    console.log('🎨 [Table Background] Changed to:', bgName);
  }

  /**
   * Show round over modal with results and accumulated scores
   * 
   * DATE: 2025-11-06 | Author: IA + Luiz Couto
   * Task 3.2.1, 3.3.1: Modal de fim de rodada com auto-transição
   */
  public showRoundOverModal(
    roundNumber: number,
    winner: string | null,
    winnerTeam: 'A' | 'B' | undefined,
    pointsEarned: number,
    reason: 'bateu' | 'trancado',
    currentScores: any,
    goalReached: boolean,
    nextRoundIn: number
  ): void {
    console.log(`🏁 [2025-11-06] Showing round over modal for round ${roundNumber}`);

    let modalHTML = `
      <div id="round-over-modal" class="modal" style="display: flex;">
        <div class="modal-content" style="max-width: 600px; text-align: center;">
          <h2 style="color: #4caf50; margin-bottom: 20px;">🏁 Rodada ${roundNumber} Finalizada!</h2>
          
          <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 20px; border-radius: 8px; margin-bottom: 20px;">
            <p style="font-size: 14px; color: #ddd; margin: 0 0 10px 0;">Motivo: ${reason === 'bateu' ? '✅ Bateu (jogou todas as peças)' : '🔒 Trancado (jogo bloqueado)'}</p>
            
            ${winner ? `
              <p style="font-size: 18px; color: #fff; margin: 10px 0;">
                ${winnerTeam ? `🎯 <strong>Time ${winnerTeam}</strong> ganhou!` : `🎯 <strong>${this.gameState?.players?.[winner]?.name || 'Jogador'}</strong> ganhou!`}
              </p>
              <p style="font-size: 28px; font-weight: bold; color: #ffeb3b; margin: 10px 0;">+${pointsEarned} pontos</p>
            ` : `
              <p style="font-size: 18px; color: #fff; margin: 10px 0;">🤝 Empate!</p>
            `}
          </div>

          <div style="background: #333; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
            <h3 style="color: #fff; margin-top: 0;">📊 Placar Acumulado:</h3>
            ${currentScores.teamA !== undefined ? `
              <div style="display: flex; justify-content: space-around; margin: 15px 0;">
                <div style="background: ${currentScores.teamA >= ((this.gameState?.pointGoal ?? 50)) ? '#ff6b6b' : '#4caf50'}; padding: 15px 30px; border-radius: 6px;">
                  <p style="margin: 0; color: #ddd; font-size: 12px;">Time A</p>
                  <p style="margin: 0; color: #fff; font-size: 28px; font-weight: bold;">${currentScores.teamA}</p>
                </div>
                <div style="background: ${currentScores.teamB >= ((this.gameState?.pointGoal ?? 50)) ? '#ff6b6b' : '#764ba2'}; padding: 15px 30px; border-radius: 6px;">
                  <p style="margin: 0; color: #ddd; font-size: 12px;">Time B</p>
                  <p style="margin: 0; color: #fff; font-size: 28px; font-weight: bold;">${currentScores.teamB}</p>
                </div>
              </div>
            ` : `
              <div style="max-height: 200px; overflow-y: auto;">
                ${Object.entries(currentScores.players || {}).map(([playerId, score]: [string, any]) => {
                  const player = this.gameState?.players?.[playerId];
                  return `
                    <div style="display: flex; justify-content: space-between; padding: 8px; border-bottom: 1px solid #555; align-items: center;">
                      <span style="color: #fff;">${player?.name || 'Desconhecido'}</span>
                      <span style="color: #ffeb3b; font-weight: bold; font-size: 18px;">${score} pts</span>
                    </div>
                  `;
                }).join('')}
              </div>
            `}
            
            <p style="color: #aaa; margin: 15px 0 0 0; font-size: 12px;">Meta: ${(this.gameState?.pointGoal ?? 50)} pontos</p>
          </div>

          ${!goalReached ? `
            <div style="background: #2196F3; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
              <p style="color: #fff; margin: 0; font-size: 14px;">Próxima rodada em...</p>
              <div id="round-countdown" style="color: #ffeb3b; font-size: 32px; font-weight: bold; margin: 10px 0;">${nextRoundIn}s</div>
            </div>
          ` : `
            <div style="background: #4caf50; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
              <p style="color: #fff; margin: 0; font-size: 16px; font-weight: bold;">🏆 META ATINGIDA! JOGO FINALIZADO!</p>
            </div>
          `}

          <button id="close-round-modal-btn" class="btn btn-primary" style="width: 100%; padding: 12px;">OK</button>
        </div>
      </div>
    `;

    // Remove old modal if exists
    const oldModal = document.getElementById('round-over-modal');
    if (oldModal) oldModal.remove();

    // Insert new modal
    const gameScreen = document.getElementById('game-screen');
    if (gameScreen) {
      try {
        gameScreen.insertAdjacentHTML('beforeend', modalHTML);
        
        // Setup countdown if not goal reached
        if (!goalReached) {
          this.setupRoundCountdown(nextRoundIn);
        }

        // Close button
        document.getElementById('close-round-modal-btn')?.addEventListener('click', () => {
          const modal = document.getElementById('round-over-modal');
          if (modal) {
            modal.classList.add('hidden');
          }
        });
      } catch (error) {
        console.error(`❌ [2025-11-06] Erro ao exibir modal de fim de rodada:`, error);
        this.showError('Erro ao exibir resultado da rodada');
      }
    }
  }

  /**
   * Setup countdown timer for next round
   * 
   * DATE: 2025-11-06 | Author: IA + Luiz Couto
   * Task 3.3.1: Auto-transição com countdown visual
   */
  private setupRoundCountdown(seconds: number): void {
    let remaining = seconds;
    const countdownEl = document.getElementById('round-countdown');

    const updateCountdown = () => {
      if (countdownEl) {
        countdownEl.textContent = `${remaining}s`;
      }
      
      remaining--;
      
      if (remaining >= 0) {
        setTimeout(updateCountdown, 1000);
      } else {
        // Auto close modal when countdown ends
        const modal = document.getElementById('round-over-modal');
        if (modal) {
          modal.classList.add('hidden');
          setTimeout(() => modal.remove(), 300);
        }
      }
    };

    updateCountdown();
  }

  /**
   * Show round starting countdown
   * 
   * DATE: 2025-11-06 | Author: IA + Luiz Couto
   * Task 3.4.1: Countdown visual para rodada começando
   */
  public showRoundStartingCountdown(roundNumber: number, countdown: number): void {
    console.log(`▶️ [2025-11-06] Round ${roundNumber} starting in ${countdown}s`);

    const modalHTML = `
      <div id="round-starting-modal" class="modal" style="display: flex; background: rgba(0,0,0,0.8);">
        <div class="modal-content" style="max-width: 400px; text-align: center; animation: pulse 0.5s infinite;">
          <h2 style="color: #ffeb3b; margin-bottom: 20px; font-size: 28px;">▶️ Rodada ${roundNumber}</h2>
          
          <div id="starting-countdown" style="
            font-size: 64px; 
            font-weight: bold; 
            color: #4caf50; 
            text-shadow: 0 0 10px #4caf50;
            margin: 40px 0;
            animation: pulse 1s infinite;
          ">${countdown}</div>

          <p style="color: #aaa; font-size: 14px;">Preparando para a próxima rodada...</p>
        </div>
      </div>
    `;

    // Remove old modal if exists
    const oldModal = document.getElementById('round-starting-modal');
    if (oldModal) oldModal.remove();

    // Insert new modal
    const gameScreen = document.getElementById('game-screen');
    if (gameScreen) {
      try {
        gameScreen.insertAdjacentHTML('beforeend', modalHTML);
        
        // Setup countdown
        let remaining = countdown;
        const countdownEl = document.getElementById('starting-countdown');

        const updateCountdown = () => {
          if (countdownEl) {
            countdownEl.textContent = `${remaining}`;
          }
          
          remaining--;
          
          if (remaining >= 0) {
            setTimeout(updateCountdown, 1000);
          } else {
            // Auto close modal when countdown ends
            const modal = document.getElementById('round-starting-modal');
            if (modal) {
              modal.classList.add('hidden');
              setTimeout(() => modal.remove(), 300);
            }
          }
        };

        updateCountdown();
      } catch (error) {
        console.error(`❌ [2025-11-06] Erro ao exibir countdown de rodada:`, error);
        this.showError('Erro ao exibir countdown da próxima rodada');
      }
    }
  }

  /**
   * Update accumulated scores display in the header
   * 
   * DATE: 2025-11-06 | Author: IA + Luiz Couto
   * Task 3.1.1, 3.5.1: Placar acumulado sempre visível e sincronizado
   * OPTIMIZED: 2025-11-06 | Code Review - Memoization para evitar DOM rebuild desnecessário
   */
  private lastScoresHTML: string = '';

  /**
   * DATE: 2025-11-09 | Author: IA + Luiz Couto
   * DEPRECATED: This method is no longer used
   * Use renderTeamScores() instead which uses static HTML elements
   */
  public updateAccumulatedScores(): void {
    if (!this.gameState) return;

    let scoresHTML = '';

    if (this.gameState.gameMode === 'partnership' && this.gameState.teamScores) {
      // Partnership mode - show team scores
      scoresHTML = `
        <div style="display: flex; gap: 20px; justify-content: center; padding: 10px 0;">
          <div style="text-align: center;">
            <span style="color: #aaa; font-size: 12px;">Time A</span>
            <div style="font-size: 20px; font-weight: bold; color: #4caf50;">${this.gameState.teamScores.teamA}</div>
          </div>
          <div style="color: #666;">vs</div>
          <div style="text-align: center;">
            <span style="color: #aaa; font-size: 12px;">Time B</span>
            <div style="font-size: 20px; font-weight: bold; color: #764ba2;">${this.gameState.teamScores.teamB}</div>
          </div>
        </div>
      `;
    } else if (this.gameState.playerScores && Object.keys(this.gameState.playerScores).length > 0) {
      // Individual mode - show player scores
      scoresHTML = `
        <div style="display: flex; gap: 15px; justify-content: center; padding: 10px 0; flex-wrap: wrap;">
          ${Object.entries(this.gameState.playerScores).map(([playerId, score]: [string, any]) => {
            const player = this.gameState?.players?.[playerId];
            const isCurrentPlayer = playerId === this.playerId;
            return `
              <div style="text-align: center; opacity: ${isCurrentPlayer ? '1' : '0.7'};">
                <span style="color: ${isCurrentPlayer ? '#ffeb3b' : '#aaa'}; font-size: 11px;">${player?.name || 'Desconhecido'}</span>
                <div style="font-size: 18px; font-weight: bold; color: ${isCurrentPlayer ? '#ffeb3b' : '#4caf50'};">${score}</div>
              </div>
            `;
          }).join('')}
        </div>
      `;
    }

    // DATE: 2025-11-06 | Author: IA + Luiz Couto
    // Performance: Apenas atualizar DOM se conteúdo realmente mudou
    if (scoresHTML === this.lastScoresHTML) {
      return;
    }
    this.lastScoresHTML = scoresHTML;

    // Update or create scores container
    let scoresContainer = document.getElementById('accumulated-scores');
    if (!scoresContainer) {
      const gameHeader = document.getElementById('game-header');
      if (gameHeader) {
        scoresContainer = document.createElement('div');
        scoresContainer.id = 'accumulated-scores';
        gameHeader.appendChild(scoresContainer);
      }
    }

    if (scoresContainer) {
      try {
        scoresContainer.innerHTML = scoresHTML;
      } catch (error) {
        console.error(`❌ [2025-11-06] Erro ao atualizar placar acumulado:`, error);
      }
    }
  }

  /**
   * Reset accumulated scores on restart
   *
   * DATE: 2025-11-06 | Author: IA + Luiz Couto
   * Task 3.5.1: resetAccumulatedScores() no handleRestart client-side
   */
  public resetAccumulatedScores(): void {
    console.log('🔄 [2025-11-06] Resetting accumulated scores display');
    const scoresContainer = document.getElementById('accumulated-scores');
    if (scoresContainer) {
      scoresContainer.innerHTML = '';
    }
  }

  // ============================================================================
  // DATE: 2025-12-17 | Author: IA + Luiz Couto
  // PLAYER PROFILE TOOLTIPS - Friend System Integration
  // ============================================================================

  /**
   * Set the current player's identity for friend system operations
   * Called from main.ts after user identity is resolved
   */
  public setPlayerIdentity(netcartasId: number | null, nickname: string): void {
    this.myNetcartasId = netcartasId;
    this.myNickname = nickname;
    console.log(`👤 [2025-12-17] GameUI: Player identity set - ${nickname} (${netcartasId})`);
  }

  /**
   * Setup player profile popups using custom modal
   * Called after players are rendered
   *
   * DATE: 2025-12-17 | Author: IA + Luiz Couto
   * Replaced Tippy.js with custom PlayerProfileModal for better control
   */
  private setupPlayerTooltips(): void {
    // Find all player badge containers
    const positions = ['north', 'south', 'east', 'west'];

    // Get the modal instance and configure it
    const modal = getPlayerProfileModal();
    modal.setMyIdentity(this.myNetcartasId, this.myNickname);

    // Setup modal callbacks
    // DATE: 2025-12-20 | Updated to use onCreateAccountWithAction
    modal.setCallbacks({
      onSendMessage: (player) => {
        console.log(`💬 [2025-12-17] Send message to ${player.name}`);
        this.showGameMessage(`Mensagem para ${player.name} (em breve)`);
      },
      onCreateAccountWithAction: (pendingAction: PendingActionData) => {
        console.log(`📝 [2025-12-20] Create account requested with action: ${pendingAction.type}`);
        // No jogo, mostramos uma mensagem direcionando para o lobby
        // A criação de conta completa está disponível no lobby
        this.showGameMessage('Volte ao lobby para criar sua conta e adicionar amigos!');
      }
    });

    // DATE: 2025-12-19 | Author: Claude + Luiz Couto
    // BUG FIX: Event was on entire container, blocking clicks on domino pieces
    // Now event is only on avatar and player name elements
    positions.forEach(position => {
      const container = document.getElementById(`player-${position}`);
      if (!container || !container.innerHTML.trim()) return;

      // Get player data from the container's data attribute
      const playerDataStr = container.getAttribute('data-player');
      if (!playerDataStr) return;

      try {
        const playerData = JSON.parse(playerDataStr) as PlayerData;

        // Create click handler
        const clickHandler = (e: Event) => {
          e.preventDefault();
          e.stopPropagation();
          console.log(`👆 [2025-12-19] Player avatar/name clicked: ${playerData.name}`);
          modal.open(playerData);
        };

        // DATE: 2025-12-19 | Author: Claude + Luiz Couto
        // Find clickable elements inside the container (avatar and name only)
        const clickableElements = [
          container.querySelector('.player-avatar-img'),
          container.querySelector('.player-avatar-initials'),
          container.querySelector('.player-name-compact')
        ].filter(el => el !== null) as HTMLElement[];

        // Remove existing handlers from all clickable elements
        clickableElements.forEach(element => {
          const existingHandler = (element as any).__profileClickHandler;
          if (existingHandler) {
            element.removeEventListener('click', existingHandler);
          }
          // Add click listener to this element
          element.addEventListener('click', clickHandler);
          (element as any).__profileClickHandler = clickHandler;
          // Add cursor pointer to indicate clickable
          element.style.cursor = 'pointer';
        });

        console.log(`💬 [2025-12-19] GameUI: Profile click setup for ${position} - ${playerData.name} (${clickableElements.length} elements)`);

      } catch (error) {
        console.error(`❌ [2025-12-19] Error parsing player data for ${position}:`, error);
      }
    });

    console.log(`💡 [2025-12-19] Setup ${positions.length} player profile click handlers (avatar/name only)`);
  }

  /**
   * Setup click handler for player's own profile in footer
   * DATE: 2025-12-19 | Author: Claude + Luiz Couto
   * Allows player to view their own profile by clicking avatar/name in footer
   */
  private setupFooterProfileClick(player: any): void {
    const avatarFooter = document.getElementById('player-avatar-footer');
    const nameDisplay = document.getElementById('player-name-display');

    // Get modal instance
    const modal = getPlayerProfileModal();

    // Create player data for modal
    const playerData = {
      id: this.playerId || 'self',
      name: player.name,
      netcartasId: player.netcartasId || this.myNetcartasId || null,
      profileImageURL: player.profileImageURL || null
    };

    // Create click handler
    const clickHandler = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      console.log(`👆 [2025-12-19] Own profile clicked: ${player.name}`);
      modal.open(playerData);
    };

    // Setup click on avatar
    if (avatarFooter) {
      // Remove existing handler
      const existingHandler = (avatarFooter as any).__profileClickHandler;
      if (existingHandler) {
        avatarFooter.removeEventListener('click', existingHandler);
      }
      avatarFooter.addEventListener('click', clickHandler);
      (avatarFooter as any).__profileClickHandler = clickHandler;
      avatarFooter.style.cursor = 'pointer';
    }

    // Setup click on name
    if (nameDisplay) {
      // Remove existing handler
      const existingHandler = (nameDisplay as any).__profileClickHandler;
      if (existingHandler) {
        nameDisplay.removeEventListener('click', existingHandler);
      }
      nameDisplay.addEventListener('click', clickHandler);
      (nameDisplay as any).__profileClickHandler = clickHandler;
      nameDisplay.style.cursor = 'pointer';
    }

    console.log(`💬 [2025-12-19] Footer profile click setup for ${player.name}`);
  }

  /**
   * Show a temporary message in the game
   */
  private showGameMessage(message: string): void {
    // Use existing chat to show message, or create a toast
    const chatMessages = document.getElementById('chat-messages');
    if (chatMessages) {
      const msgDiv = document.createElement('div');
      msgDiv.className = 'chat-message system-message';
      msgDiv.innerHTML = `<span class="message-text">${message}</span>`;
      chatMessages.appendChild(msgDiv);
      chatMessages.scrollTop = chatMessages.scrollHeight;
    }
  }
}